import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface EmptyStateProps {
  /** A lucide icon element. */
  icon?: ReactNode;
  /** What will appear here: "No sessions yet". */
  title: ReactNode;
  /** How to make it appear: "Sessions show up when Claude Code runs in a watched project." */
  description?: ReactNode;
  /** The next step, usually one Button. */
  action?: ReactNode;
  /** compact: inside a panel or list. page: the whole content area. */
  size?: 'compact' | 'page';
  className?: string;
}

/** A designed empty state: what will appear here, how to make it appear, and the action that does it. */
export function EmptyState({ icon, title, description, action, size = 'compact', className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'page' ? 'min-h-96 gap-3 px-6 py-16' : 'gap-2 px-6 py-10',
        className,
      )}
    >
      {icon && (
        <div
          className={cn(
            'mb-1 flex items-center justify-center rounded-card border border-border bg-control text-fg-subtle shadow-control',
            size === 'page' ? 'size-12 [&_svg]:size-6' : 'size-10 [&_svg]:size-5',
          )}
          aria-hidden
        >
          {icon}
        </div>
      )}
      <p className={cn('font-medium text-fg', size === 'page' ? 'text-lead' : 'text-body')}>{title}</p>
      {description && <p className="max-w-(--layout-width-interstitial) text-ui text-fg-muted">{description}</p>}
      {action && <div className="mt-2 flex items-center gap-2">{action}</div>}
    </div>
  );
}
